import { Color, OneFactor, PerspectiveCamera, Vector4, ZeroFactor } from "three";
import { GTAOPass } from "three/examples/jsm/postprocessing/GTAOPass.js";
import { FullScreenQuad } from "three/examples/jsm/postprocessing/Pass.js";
import { getGtaoSettings } from "./GtaoSettings";
const DEFAULT_SETTINGS = getGtaoSettings();
export class AmbientOcclusionRenderer {
    constructor(renderer) {
        this.renderer = renderer;
        this.viewCamera = new PerspectiveCamera();
        this.viewport = new Vector4();
        this.targetViewport = new Vector4();
        this.clearColor = new Color();
    }
    render(scene, camera, enabled, settings = DEFAULT_SETTINGS) {
        const renderer = this.renderer;
        renderer.render(scene, camera);
        if (!enabled) {
            this.dispose();
            return;
        }
        const target = renderer.getRenderTarget();
        const xrEnabled = renderer.xr.enabled;
        const autoClear = renderer.autoClear;
        const shadowAutoUpdate = renderer.shadowMap.autoUpdate;
        const overrideMaterial = scene.overrideMaterial;
        const clearAlpha = renderer.getClearAlpha();
        renderer.getClearColor(this.clearColor);
        renderer.getCurrentViewport(this.viewport);
        if (target)
            this.targetViewport.copy(target.viewport);
        try {
            // Keep Three.js' normal color rendering and XR framebuffer intact. Only
            // compute AO offscreen, then multiply it onto each eye's existing image.
            renderer.xr.enabled = false;
            renderer.shadowMap.autoUpdate = false;
            if (xrEnabled && renderer.xr.isPresenting) {
                for (const eye of renderer.xr.getCamera().cameras) {
                    if (!eye.viewport) {
                        throw new Error("GTAO requires a viewport for each XR eye");
                    }
                    this.renderView(scene, eye, eye.viewport, settings);
                }
            }
            else {
                this.renderView(scene, camera, this.viewport, settings);
            }
        }
        finally {
            renderer.xr.enabled = xrEnabled;
            renderer.autoClear = autoClear;
            renderer.shadowMap.autoUpdate = shadowAutoUpdate;
            renderer.setClearColor(this.clearColor, clearAlpha);
            scene.overrideMaterial = overrideMaterial;
            if (target)
                target.viewport.copy(this.viewport);
            renderer.setRenderTarget(target);
            if (target)
                target.viewport.copy(this.targetViewport);
        }
    }
    dispose() {
        if (this.resources) {
            const { pass, quad } = this.resources;
            // GTAOPass.dispose() does not currently release these two materials.
            pass.gtaoMaterial.dispose();
            pass.blendMaterial.dispose();
            pass.dispose();
            quad.dispose();
            this.resources = undefined;
        }
    }
    renderView(scene, camera, viewport, settings) {
        const renderer = this.renderer;
        const target = renderer.getRenderTarget();
        const width = Math.max(1, Math.floor(viewport.z));
        const height = Math.max(1, Math.floor(viewport.w));
        // A detached copy retains the eye's asymmetric projection and world pose
        // without its XR viewport leaking into the offscreen normal/depth render.
        this.viewCamera.copy(camera, false);
        this.viewCamera.viewport = undefined;
        this.viewCamera.matrixWorldAutoUpdate = false;
        if (!this.resources) {
            const pass = new GTAOPass(scene, this.viewCamera, width, height);
            pass.output = GTAOPass.OUTPUT.Off;
            pass.blendMaterial.blendSrcAlpha = ZeroFactor;
            pass.blendMaterial.blendDstAlpha = OneFactor;
            this.resources = { pass, quad: new FullScreenQuad(pass.blendMaterial) };
        }
        const { pass, quad } = this.resources;
        pass.scene = scene;
        if (!this.resources.settings || Object.entries(settings).some(([key, value]) => { var _a, _b; return ((_b = (_a = this.resources) === null || _a === void 0 ? void 0 : _a.settings) === null || _b === void 0 ? void 0 : _b[key]) !== value; })) {
            pass.updateGtaoMaterial({
                radius: settings.gtaoRadius,
                thickness: settings.gtaoThickness,
                distanceExponent: settings.gtaoDistanceExponent,
                distanceFallOff: settings.gtaoDistanceFallOff,
                scale: settings.gtaoScale,
                samples: settings.gtaoSamples,
                screenSpaceRadius: settings.gtaoScreenSpaceRadius
            });
            pass.updatePdMaterial({
                radius: settings.gtaoDenoiseRadius,
                samples: settings.gtaoDenoiseSamples,
                rings: settings.gtaoDenoiseRings,
                radiusExponent: settings.gtaoDenoiseRadiusExponent,
                lumaPhi: settings.gtaoDenoiseLumaPhi,
                depthPhi: settings.gtaoDenoiseDepthPhi,
                normalPhi: settings.gtaoDenoiseNormalPhi
            });
            pass.blendIntensity = settings.gtaoBlendIntensity;
            this.resources.settings = Object.assign({}, settings);
        }
        if (pass.width !== width || pass.height !== height)
            pass.setSize(width, height);
        // OUTPUT.Off computes GTAO and denoising without reading/writing the
        // composer's color buffers, so no additional scene-color target is needed.
        pass.render(renderer, pass.gtaoRenderTarget, pass.gtaoRenderTarget, 0, false);
        pass.blendMaterial.uniforms.tDiffuse.value = pass.pdRenderTarget.texture;
        pass.blendMaterial.uniforms.intensity.value = pass.blendIntensity;
        if (target)
            target.viewport.copy(viewport);
        renderer.setRenderTarget(target);
        renderer.autoClear = false;
        quad.render(renderer);
    }
}
